import { API_BASE_URL } from "config";

import { BaseService } from "./base-service";

/** A single recently visited item returned by the telemetry API. */
export interface RecentlyVisitedItem {
  /** Taxonomy ID of the visited item (e.g. `atn::software/ai/tool-1`). */
  itemId: string;
  /** ISO-8601 timestamp of when the visit was recorded. */
  visitedAt: string;
}

/** Shape of the GET `/telemetry/recently-visited/visits` response body. */
interface GetRecentlyVisitedResponse {
  items: RecentlyVisitedItem[];
}

/**
 * Client for the recently-visited telemetry endpoints.
 *
 * Tracks which IT Shop / AI for Me items a user has viewed so the UI can
 * display a "Recently Visited" section on the AI for Me landing page and
 * record new visits when the user clicks "Learn More" or opens a product
 * detail page.
 */
export class RecentlyVisitedService extends BaseService {
  constructor() {
    super(API_BASE_URL);
  }

  /**
   * Retrieves the user's recently visited items.
   *
   * @param source - Which surface the visits belong to (`"itshop"` or `"aiforme"`).
   * @param amount - Optional cap on the number of visits to return.
   * @returns The list of recently visited items.
   * @throws Error if the API returns a non-OK response.
   */
  public async getRecentlyVisited(source: "itshop" | "aiforme", amount?: number): Promise<RecentlyVisitedItem[]> {
    const params = new URLSearchParams({ source });
    if (amount) params.set("amount", String(amount));
    const response = await this.fetch(`/telemetry/recently-visited/visits?${params}`);
    if (!response.ok) throw new Error(`Failed to fetch recently visited: ${response.status}`);
    const data: GetRecentlyVisitedResponse = await response.json();
    return data.items ?? [];
  }

  /**
   * Records a visit for the given item.
   *
   * @param itemId - Taxonomy ID of the visited item (should include the `atn::` prefix).
   * @param visitSource - Which surface triggered the visit (`"itshop"` or `"aiforme"`).
   * @returns The recorded visit confirmation including the server-assigned timestamp.
   */
  public async recordVisit(
    itemId: string,
    visitSource: "itshop" | "aiforme"
  ): Promise<{ message: string; visitedAt: string }> {
    const response = await this.fetch("/telemetry/recently-visited/visit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ itemId, visitSource }),
    });
    if (!response.ok) {
      throw new Error(`Failed to record visit: ${response.status}`);
    }
    return response.json();
  }
}
